import { useMemo } from 'react';

import PrimaryButton from '@components/buttons/PrimaryButton';
import SecondaryButton from '@components/buttons/SecondaryButton';
import styles from '@components/fields/FormButtons.module.css';
import { clickCurrentTarget } from '@utils/keyboardUtils';

type FormState = {
  formState: {
    isSubmitting: boolean;
    isValid: boolean;
  };
  reset: () => void;
};

type Props = {
  buttonSize?: 'large';
  cancelKey?: string;
  expand?: 'full' | 'block';
  fullRow?: boolean;
  hookForm?: FormState;
  narrowMargin?: boolean;
  onCancel?: () => void;
  onSave?: () => void;
  saveDisabled?: boolean;
  saveKey?: string;
  skipCancel?: boolean;
  skipValid?: boolean;
  spinnerWhenSubmitting?: boolean;
};

const FormButtons = ({
  buttonSize,
  cancelKey = 'dictionary.cancel',
  expand,
  fullRow = false,
  hookForm,
  narrowMargin = false,
  onCancel,
  onSave,
  saveDisabled = false,
  saveKey = 'dictionary.saveChanges',
  skipCancel,
  skipValid = false,
  spinnerWhenSubmitting = false
}: Props) => {
  const saveButtonDisabled = useMemo(
    () => (!hookForm?.formState.isValid && !skipValid) || hookForm?.formState.isSubmitting || saveDisabled,
    [hookForm?.formState.isSubmitting, hookForm?.formState.isValid, saveDisabled, skipValid]
  );

  return (
    <div className={`${styles.footer} ${narrowMargin ? styles.narrowMargin : ''} ${fullRow ? styles.flex : ''}`}>
      {!skipCancel && (
        <SecondaryButton
          disabled={hookForm?.formState.isSubmitting ?? saveDisabled}
          expand={expand}
          onClick={() => {
            hookForm?.reset();
            onCancel?.();
          }}
          onKeyUp={clickCurrentTarget}
          tabIndex={0}
          textKey={cancelKey}
        />
      )}

      <PrimaryButton
        data-cy="submit-button"
        disabled={saveButtonDisabled}
        expand={expand}
        icon={hookForm?.formState.isSubmitting && spinnerWhenSubmitting ? 'spinner' : undefined}
        onClick={() => {
          onSave?.();
        }}
        onKeyUp={clickCurrentTarget}
        size={buttonSize}
        tabIndex={0}
        textKey={saveKey}
        type="submit"
      />
    </div>
  );
};

export default FormButtons;
